package dev.lingvo.site.components.editor

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import dev.lingvo.site.ds.Space
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

/**
 * The counts and the actions they imply, on one line.
 *
 * The two figures and the automation that acts on them are the same decision — "412 untranslated"
 * and "Pre-translate 412" are one sentence read in two halves — so they share a row and the action
 * sits at the inline end of the numbers rather than in a bar of its own. It wraps rather than
 * scrolls: at 320px the two tiles take the first line and the action drops beneath them, which is
 * the only arrangement where neither half is truncated.
 *
 * **The column gap is `XL`, not `SM`, because the tiles are numbers.** Two tabular figures at 21px
 * sitting 8px apart, with their captions on the line below, are read as one figure: the header's
 * headline was literally "36 of 45 9" over "Translated  Remaining". 24px is the ladder rung that
 * separates tile from tile — and tile from the read-only badge — without touching the action slot,
 * which is pushed out by its own `margin-inline-start: auto` regardless of what this gap says.
 *
 * The **row** gap stays `SM`, and that is not a compromise: the wrap here is the action dropping
 * under the numbers it acts on, which nothing ever confused for one figure. It is also the
 * difference between fitting and not — `CompactQueueHeaderStyle` caps this header at
 * `EDITOR_QUEUE_HEADER_MAX_HEIGHT_PX`, and setting both axes to 24 pushed the counts row six
 * pixels past the cap and under the first key row.
 */
val EditorQueueCountsStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .minWidth(0.px)
        .gap(rowGap = Space.SM, columnGap = Space.XL)
        .styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("flex-wrap", "wrap")
        }
}

/** The action cluster, pushed to the inline end of the counts it acts on. */
val EditorQueueActionSlotStyle = CssStyle.base {
    Modifier
        .minWidth(0.px)
        .gap(Space.XS)
        .styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("flex-wrap", "wrap")
            property("margin-inline-start", "auto")
        }
}

/** The run readout, which replaces nothing and pushes nothing: it is one more line under the bar. */
val EditorQueueRunStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .minWidth(0.px)
}

/** The bulk confirm's body: a scope sentence, the evidence, then the decision. */
val EditorBulkConfirmBodyStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .minWidth(0.px)
        .gap(Space.MD)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
        }
}

/**
 * The key names a batch is about to touch.
 *
 * A list rather than a sentence, and capped at ten with a counted remainder, because the thing a
 * reader checks before approving thirty strings is whether the *first* few are the ones they meant.
 */
val EditorBulkPreviewListStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .minWidth(0.px)
        .gap(Space.XXS)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("margin", "0")
            property("padding-inline-start", "1.1em")
            property("overflow-wrap", "anywhere")
        }
}

/**
 * "412 keys match", under the filter controls, whether or not anything is applied.
 *
 * A polite live region rather than plain text: the number changes when a `<select>` changes, which
 * is a moment a screen-reader user gets no other feedback from at all.
 */
val EditorQueueFilterResultStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .minWidth(0.px)
}
